import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  getBankStatus,
  getFinanceAccounts,
  getFinanceSummary,
  getReviewQueue,
  type BankStatus,
  type FinanceAccount,
  type FinanceSummary,
} from '../api.js';
import Income from './Income.js';
import TaxSetAside from '../lifeos/TaxSetAside.js';
import Overview from '../money/Overview.js';
import Transactions from '../money/Transactions.js';
import CashFlow from '../money/CashFlow.js';
import Recurring from '../money/Recurring.js';
import Budget from '../money/Budget.js';
import Reports from '../money/Reports.js';
import Goals from '../money/Goals.js';
import { dollars } from '../money/format.js';
import { AccountForm, ConnectBank, ImportPanel, SkeletonGroup } from '../money/parts.js';
import { ErrorState, IconButton, Page, Sheet } from '../ui/index.js';
import '../money/money.css';

/**
 * Money — everything finance in one section: four tabs across the top
 * (`/money/:tab`), net worth always in the subtitle. Each tab owns its own
 * data; the shell only knows enough to draw the chrome (net worth, whether
 * any account exists, the Review count, the bank feed).
 */

export type MoneyTab = 'overview' | 'transactions' | 'plan' | 'reports';

export const MONEY_TABS: readonly { value: MoneyTab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'transactions', label: 'Transactions' },
  { value: 'plan', label: 'Plan' },
  { value: 'reports', label: 'Reports' },
];

/** The ten-tab layout's URLs, and where each one lives now. */
export const LEGACY_TABS: Readonly<Record<string, string>> = {
  review: '/money/transactions?view=review',
  'cash-flow': '/money/reports',
  income: '/money/reports',
  budget: '/money/plan',
  recurring: '/money/plan',
  goals: '/money/plan',
  advice: '/money',
};

/** Tabs that read the bank data and so need an account first. */
const NEEDS_ACCOUNTS = new Set<MoneyTab>(['overview', 'transactions']);

export default function Money(): JSX.Element {
  const params = useParams<{ tab?: string }>();
  const legacy = params.tab ? LEGACY_TABS[params.tab] : undefined;
  if (legacy) return <Navigate to={legacy} replace />;
  return <MoneyShell tabParam={params.tab} />;
}

function MoneyShell({ tabParam }: { tabParam?: string }): JSX.Element {
  const navigate = useNavigate();
  const tab: MoneyTab = MONEY_TABS.find((t) => t.value === tabParam)?.value ?? 'overview';
  const go = (t: MoneyTab): void => navigate(t === 'overview' ? '/money' : `/money/${t}`);

  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [accounts, setAccounts] = useState<FinanceAccount[]>([]);
  const [bank, setBank] = useState<BankStatus | null>(null);
  const [reviewCount, setReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      // The Review count is a nicety (it picks Transactions' default view); a failure there shouldn't block the page.
      const [s, a, b, n] = await Promise.all([
        getFinanceSummary(),
        getFinanceAccounts(),
        getBankStatus(),
        getReviewQueue().then(
          (r) => r.total,
          () => 0,
        ),
      ]);
      setSummary(s);
      setAccounts(a);
      setBank(b);
      setReviewCount(n);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onReviewCount = useCallback((n: number) => setReviewCount(n), []);
  const empty = !loading && !error && accounts.length === 0 && NEEDS_ACCOUNTS.has(tab);

  return (
    <Page
      title="Money"
      sub={summary && accounts.length > 0 ? <>Net worth <span className="num">{dollars(summary.netWorth.netCents)}</span></> : undefined}
      actions={
        // With a live bank feed there is nothing to import by hand.
        accounts.length > 0 && !summary?.simplefin ? (
          <IconButton icon="file" label="Import CSV" onClick={() => setImporting(true)} />
        ) : undefined
      }
    >
      <TabStrip tab={tab} onChange={go} />

      {error && <ErrorState message={error} onRetry={() => void load()} />}

      {loading && NEEDS_ACCOUNTS.has(tab) ? (
        <SkeletonGroup />
      ) : empty ? (
        <ConnectBank onDone={() => void load()} />
      ) : (
        <div className="mn-tab" role="tabpanel" aria-label={MONEY_TABS.find((t) => t.value === tab)?.label}>
          {tab === 'overview' && (
            <Overview
              bank={bank}
              simplefin={summary?.simplefin ?? false}
              onChange={() => void load()}
              onAddAccount={() => setAdding(true)}
            />
          )}
          {tab === 'transactions' && (
            <Transactions
              reviewCount={reviewCount}
              onReviewCount={onReviewCount}
              onImport={() => setImporting(true)}
              onChange={() => void load()}
            />
          )}
          {tab === 'plan' && <Plan />}
          {tab === 'reports' && <MoneyReports />}
        </div>
      )}

      {importing && (
        <Sheet title="Import CSV" onClose={() => setImporting(false)}>
          <ImportPanel accounts={accounts} onDone={() => void load()} />
        </Sheet>
      )}
      {adding && (
        <Sheet title="Add account" onClose={() => setAdding(false)}>
          <AccountForm
            onDone={() => {
              setAdding(false);
              void load();
            }}
          />
        </Sheet>
      )}
    </Page>
  );
}

/** Plan — what he means to spend and save: budgets, the bills that repeat, then goals. */
function Plan(): JSX.Element {
  return (
    <>
      <section className="mn-section" aria-label="Budget">
        <div className="ui-group__head">
          <h2 className="ui-group__title">Budget</h2>
        </div>
        <Budget />
      </section>
      <section className="mn-section" aria-label="Recurring">
        <div className="ui-group__head">
          <h2 className="ui-group__title">Recurring</h2>
        </div>
        <Recurring />
      </section>
      <section className="mn-section" aria-label="Goals">
        <Goals />
      </section>
    </>
  );
}

/**
 * Reports — this period's cash flow (the Sankey and the category drill), the
 * month-by-month trend, then Income and Taxes. The trend's own category stack
 * was dropped: "Where it went" already answers that, with the drill.
 */
function MoneyReports(): JSX.Element {
  return (
    <>
      <CashFlow />
      <section className="mn-section" aria-label="Month by month">
        <Reports />
      </section>
      <Income />
      <section className="ui-groupwrap mn-tax">
        <div className="ui-group__head">
          <h2 className="ui-group__title">Taxes</h2>
        </div>
        <div className="ui-card mn-tax__card">
          <TaxSetAside />
        </div>
      </section>
    </>
  );
}

/** Four tabs fit a phone, so this is a plain strip with the active one underlined (Monarch's top nav). */
function TabStrip({ tab, onChange }: { tab: MoneyTab; onChange: (t: MoneyTab) => void }): JSX.Element {
  return (
    <div className="mn-tabs">
      <div className="mn-tabs__list" role="tablist" aria-label="Money">
        {MONEY_TABS.map((t) => {
          const on = t.value === tab;
          return (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={on}
              className={`mn-tabs__tab${on ? ' mn-tabs__tab--on' : ''}`}
              onClick={() => onChange(t.value)}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
